import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { logClientError } from "@/lib/errorLogger";

type Props = { children: ReactNode };
type State = { hasError: boolean; error: Error | null };

/**
 * Global error boundary. Catches any uncaught render error anywhere in the
 * tree and shows a friendly fallback rather than a blank white screen.
 * Logs to console and to `error_events` for Sentry-style admin visibility.
 */
class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info.componentStack);
    void logClientError({
      kind: "react_error_boundary",
      message: error.message || String(error),
      stack: error.stack,
      component_stack: info.componentStack,
    });
  }

  private reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background px-4 py-12">
        <div className="max-w-md w-full text-center space-y-5">
          <div className="mx-auto w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-destructive" aria-hidden />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              We hit an unexpected error. Try reloading the page. If the problem persists, contact support@getpetvet.co.uk.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button onClick={() => { this.reset(); window.location.reload(); }} className="gap-2">
              <RefreshCw className="w-4 h-4" aria-hidden /> Reload page
            </Button>
            <Button variant="outline" onClick={() => { this.reset(); window.location.assign("/"); }} className="gap-2">
              <Home className="w-4 h-4" aria-hidden /> Go home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
